import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [tokens, setTokens] = useState(null);
	const [loading, setLoading] = useState(true);
	const userCache = React.useRef(new Map());

	// Check for existing session on mount
	useEffect(() => {
		const checkAuth = () => {
			try {
				const savedTokens = localStorage.getItem("mycron_tokens");
				const savedUser = localStorage.getItem("mycron_user");

				if (savedTokens && savedUser) {
					const parsedUser = JSON.parse(savedUser);
					setTokens(JSON.parse(savedTokens));
					setUser(parsedUser);
				}
			} catch (error) {
				console.error("Error restoring auth state:", error);
			} finally {
				setLoading(false);
			}
		};

		checkAuth();
	}, []);

	const login = async ({ tokens, user }) => {
		try {
			// Store original data first
			setTokens(tokens);
			setUser(user);
			localStorage.setItem("mycron_tokens", JSON.stringify(tokens));
			localStorage.setItem("mycron_user", JSON.stringify(user));

			// Attempt to cache the image separately, but don't modify the original URL
			if (user?.picture) {
				try {
					const img = new Image();
					img.src = user.picture;
					userCache.current.set("profilePictureUrl", user.picture);
				} catch (error) {
					console.error("Failed to cache profile picture:", error);
				}
			}

			return true;
		} catch (error) {
			console.error("Login error:", error);
			return false;
		}
	};

	const logout = () => {
		setTokens(null);
		setUser(null);
		userCache.current.clear();
		localStorage.removeItem("mycron_tokens");
		localStorage.removeItem("mycron_user");
	};

	const getCachedPicture = () => userCache.current.get("profilePictureUrl");

	return (
		<AuthContext.Provider
			value={{
				user,
				tokens,
				loading,
				isAuthenticated: !!user,
				login,
				logout,
				getCachedPicture,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
